import { isAdjacentPosition } from './grid'
import type { GridPosition, LetterBoard } from './types'

function normalizeDictionaryWord(word: string) {
  return word.trim().toLowerCase()
}

export type DictionaryLexicon = {
  getBoardWord: (board: LetterBoard, selection: GridPosition[]) => string
  isDictionaryWord: (word: string) => boolean
}

export type LetterLexicon = DictionaryLexicon & {
  findPlayableWords: (
    board: LetterBoard,
    options: {
      allowDiagonals: boolean
      maxWordLength?: null | number
      forbidAdjacency?: boolean
      limit?: number
      minWordLength: number
      requireAdjacency?: boolean
      repeatTileMinGap?: number
    },
  ) => Array<{ path: GridPosition[]; word: string }>
}

function findLastPathIndex(path: GridPosition[], position: GridPosition) {
  for (let index = path.length - 1; index >= 0; index -= 1) {
    const entry = path[index]
    if (entry && entry.row === position.row && entry.col === position.col) {
      return index
    }
  }

  return -1
}

function buildNormalizedWordSets(rawWords: string[]) {
  const normalizedWords = rawWords
    .map(normalizeDictionaryWord)
    .filter(word => /^[a-z]+$/.test(word))
  const wordSet = new Set(normalizedWords)
  const uniqueWords = [...wordSet]
  const prefixSet = new Set<string>()

  for (const word of normalizedWords) {
    for (let prefixLength = 1; prefixLength < word.length; prefixLength += 1) {
      prefixSet.add(word.slice(0, prefixLength))
    }
  }

  return { prefixSet, uniqueWords, wordSet }
}

/** Used by the lexicon code generator (serializes `prefixSet` for the app bundle). */
export function buildPrecomputedLetterLexiconInputs(rawWords: string[]): {
  prefixes: string[]
  uniqueWords: string[]
} {
  const { prefixSet, uniqueWords } = buildNormalizedWordSets(rawWords)
  return { prefixes: [...prefixSet], uniqueWords }
}

export function createDictionaryLexiconFromWordList(
  uniqueWords: readonly string[],
): DictionaryLexicon {
  const wordSet = new Set(
    uniqueWords.map(normalizeDictionaryWord).filter(word => /^[a-z]+$/.test(word)),
  )

  function isDictionaryWord(word: string) {
    return wordSet.has(normalizeDictionaryWord(word))
  }

  function getBoardWord(board: LetterBoard, selection: GridPosition[]) {
    return selection
      .map(position => board[position.row]?.[position.col]?.letter ?? '')
      .join('')
  }

  return {
    getBoardWord,
    isDictionaryWord,
  }
}

function letterLexiconFromWordAndPrefixSets(
  uniqueWords: readonly string[],
  wordSet: Set<string>,
  prefixSet: Set<string>,
): LetterLexicon {
  function isDictionaryWord(word: string) {
    return wordSet.has(normalizeDictionaryWord(word))
  }

  function hasPrefix(prefix: string) {
    return prefixSet.has(normalizeDictionaryWord(prefix))
  }

  function getBoardWord(board: LetterBoard, selection: GridPosition[]) {
    return selection
      .map(position => board[position.row]?.[position.col]?.letter ?? '')
      .join('')
  }

  function findPlayableWords(
    board: LetterBoard,
    {
      allowDiagonals,
      maxWordLength = null,
      forbidAdjacency = false,
      minWordLength,
      limit = Number.POSITIVE_INFINITY,
      requireAdjacency = true,
      repeatTileMinGap = 0,
    }: {
      allowDiagonals: boolean
      maxWordLength?: null | number
      forbidAdjacency?: boolean
      limit?: number
      minWordLength: number
      requireAdjacency?: boolean
      repeatTileMinGap?: number
    },
  ) {
    if (!requireAdjacency && !forbidAdjacency) {
      const letterPositions = new Map<string, GridPosition[]>()

      for (let row = 0; row < board.length; row += 1) {
        for (let col = 0; col < (board[row]?.length ?? 0); col += 1) {
          const letter = board[row]?.[col]?.letter?.toLowerCase()
          if (!letter) {
            continue
          }

          const positions = letterPositions.get(letter)
          if (positions) {
            positions.push({ col, row })
          } else {
            letterPositions.set(letter, [{ col, row }])
          }
        }
      }

      const found: Array<{ path: GridPosition[]; word: string }> = []

      for (const word of uniqueWords) {
        if (word.length < minWordLength) {
          continue
        }

        if (maxWordLength !== null && word.length > maxWordLength) {
          continue
        }

        let isPlayable = true
        const path: GridPosition[] = []
        const usedKeys = new Set<string>()
        for (const letter of word) {
          const positions = letterPositions.get(letter)
          if (!positions || positions.length === 0) {
            isPlayable = false
            break
          }

          const nextPosition =
            repeatTileMinGap > 0
              ? positions.find(position => !usedKeys.has(`${position.row}:${position.col}`))
              : positions[0]
          if (!nextPosition) {
            isPlayable = false
            break
          }

          path.push(nextPosition)
          if (repeatTileMinGap > 0) {
            usedKeys.add(`${nextPosition.row}:${nextPosition.col}`)
          }
        }

        if (!isPlayable) {
          continue
        }

        found.push({ path, word })
        if (found.length >= limit) {
          break
        }
      }

      return found
    }

    const rows = board.length
    const cols = board[0]?.length ?? 0
    const allPositions = Array.from({ length: rows }, (_, row) =>
      Array.from({ length: cols }, (_, col) => ({ col, row })),
    ).flat()
    const offsets = allowDiagonals
      ? [-1, 0, 1].flatMap(rowOffset =>
          [-1, 0, 1]
            .filter(colOffset => rowOffset !== 0 || colOffset !== 0)
            .map(colOffset => ({ colOffset, rowOffset })),
        )
      : [
          { colOffset: 0, rowOffset: -1 },
          { colOffset: 1, rowOffset: 0 },
          { colOffset: 0, rowOffset: 1 },
          { colOffset: -1, rowOffset: 0 },
        ]
    const found = new Map<string, GridPosition[]>()

    function search(row: number, col: number, prefix: string, path: GridPosition[]) {
      if (found.size >= limit) {
        return
      }

      const cell = board[row]?.[col]
      if (!cell) {
        return
      }

      const revisitIndex = findLastPathIndex(path, { col, row })
      if (revisitIndex !== -1 && path.length - revisitIndex - 1 < repeatTileMinGap) {
        return
      }

      const letter = cell.letter
      const word = `${prefix}${letter.toLowerCase()}`
      if (!hasPrefix(word) && !isDictionaryWord(word)) {
        return
      }

      if (maxWordLength !== null && word.length > maxWordLength) {
        return
      }

      path.push({ col, row })

      if (
        word.length >= minWordLength &&
        isDictionaryWord(word) &&
        !found.has(word)
      ) {
        found.set(word, [...path])
        if (found.size >= limit) {
          path.pop()
          return
        }
      }

      if (maxWordLength !== null && word.length >= maxWordLength) {
        path.pop()
        return
      }

      const nextPositions = requireAdjacency
        ? offsets
            .map(({ colOffset, rowOffset }) => ({
              col: col + colOffset,
              row: row + rowOffset,
            }))
            .filter(
              position =>
                position.row >= 0 &&
                position.row < rows &&
                position.col >= 0 &&
                position.col < cols,
            )
        : allPositions

      for (const nextPosition of nextPositions) {
        const isAdjacentStep = isAdjacentPosition(
          { col, row },
          nextPosition,
          allowDiagonals,
        )
        if (requireAdjacency && !isAdjacentStep) {
          continue
        }

        if (
          forbidAdjacency &&
          isAdjacentPosition({ col, row }, nextPosition, false)
        ) {
          continue
        }

        search(nextPosition.row, nextPosition.col, word, path)
      }

      path.pop()
    }

    for (let row = 0; row < rows; row += 1) {
      for (let col = 0; col < cols; col += 1) {
        search(row, col, '', [])
        if (found.size >= limit) {
          break
        }
      }
    }

    return [...found.entries()].map(([word, path]) => ({ path, word }))
  }

  return {
    findPlayableWords,
    getBoardWord,
    isDictionaryWord,
  }
}

export function createLetterLexiconFromPrecomputed(
  uniqueWords: readonly string[],
  prefixes: readonly string[],
): LetterLexicon {
  const wordSet = new Set(uniqueWords)
  const prefixSet = new Set(prefixes)
  return letterLexiconFromWordAndPrefixSets(uniqueWords, wordSet, prefixSet)
}

export function createLetterLexicon(rawWords: string[]): LetterLexicon {
  const { prefixSet, uniqueWords, wordSet } = buildNormalizedWordSets(rawWords)
  return letterLexiconFromWordAndPrefixSets(uniqueWords, wordSet, prefixSet)
}
